// SaaSGovernanceView.jsx - SaaS Governance Module for Logworthy
// Drop this into src/components/SaaSGovernanceView.jsx
import React, { useState, useMemo, useEffect } from 'react'
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer
} from 'recharts'
import {
  Search, Shield, AlertTriangle, Plus, X, Edit2, Eye,
  Calendar, Home, Server, Key, ChevronDown, ChevronUp, CheckCircle,
  Clock, Filter, ArrowUpDown, Lock, Layers, BarChart3, ChevronRight,
  Database, Loader2, Download, Upload, ClipboardCheck, UserMinus, History
} from 'lucide-react'
import * as XLSX from 'xlsx'
import { useAuth } from '../contexts/AuthContext'
import { useTenant } from '../contexts/TenantContext'
import { supabase } from '../lib/supabase'

/* ─── CONSTANTS ──────────────────────────────────────────────────── */
const CATEGORIES = [
  "Identity & Access", "Cloud Infrastructure", "Endpoint Management", "Endpoint Security",
  "Finance & Equity", "Source Code & Prod Access", "Collaboration", "File Storage",
  "Sales & CRM", "Support", "Messaging", "Web & CMS", "Commerce", "Analytics",
  "DNS & Domains", "Backup & DR", "Network Security", "Dev Tooling", "Web Platform",
  "Legal", "Sales Enablement", "HR", "HR Compliance", "Security", "Media"
]
const RISK_LEVELS = ["Tier 1 - Critical", "Tier 1 - High", "Tier 2 - Medium", "Tier 3 - Low"]
const SENSITIVITY_LEVELS = ["High", "Medium", "Low", "Public"]
const AUTH_METHODS = ["SSO (SAML)", "SSO (OIDC)", "Local Auth", "API Key", "Certificate", "Federated"]
const PRODUCTION_IMPACTS = ["Critical", "High", "Medium", "Low", "None"]
const ACCOUNT_TYPES = ["Named User", "Service Account", "Shared Account", "API/Bot", "Emergency"]
const ACCESS_LEVELS = ["Super Admin", "Admin", "Power User", "Standard", "Read Only", "Custom"]
const REVIEW_FREQUENCIES = ["Monthly", "Quarterly", "Semi-Annual", "Annual"]
const DATA_TYPES = ["PII", "PHI", "PCI", "Financial", "IP", "Employee Data", "Customer Data", "Telemetry", "Credentials", "Source Code", "Contracts", "Analytics", "API Keys"]
const BILLING_FREQUENCIES = ["Monthly", "Annual", "Per-check", "Usage-based", "One-time"]

const RISK_COLORS = {
  "Tier 1 - Critical": "#7c2d12",
  "Tier 1 - High": "#dc2626",
  "Tier 2 - Medium": "#ca8a04",
  "Tier 3 - Low": "#16a34a"
}
const RISK_BG = {
  "Tier 1 - Critical": "bg-red-50 border-red-200 text-red-800",
  "Tier 1 - High": "bg-red-50 border-red-200 text-red-600",
  "Tier 2 - Medium": "bg-yellow-50 border-yellow-200 text-yellow-700",
  "Tier 3 - Low": "bg-green-50 border-green-200 text-green-700"
}
const SENSITIVITY_COLORS = { High: "#dc2626", Medium: "#ea580c", Low: "#2563eb", Public: "#16a34a" }
const SENSITIVITY_BG = {
  High: "bg-red-50 border-red-200 text-red-600",
  Medium: "bg-orange-50 border-orange-200 text-orange-600",
  Low: "bg-blue-50 border-blue-200 text-blue-600",
  Public: "bg-green-50 border-green-200 text-green-600"
}

const CATEGORY_GROUPS = {
  "Security & Identity": ["Identity & Access", "Endpoint Security", "Network Security", "Security"],
  "Infrastructure": ["Cloud Infrastructure", "Endpoint Management", "Backup & DR", "DNS & Domains"],
  "Engineering": ["Source Code & Prod Access", "Dev Tooling", "Web Platform"],
  "Business Ops": ["Finance & Equity", "HR", "HR Compliance", "Legal", "Sales Enablement", "Sales & CRM"],
  "Collaboration": ["Collaboration", "Messaging", "Media", "Support"],
  "Data & Analytics": ["Analytics"],
  "Web & Commerce": ["Web & CMS", "Commerce", "File Storage"]
}
const GROUP_COLORS = {
  "Security & Identity": "#dc2626",
  "Infrastructure": "#2563eb",
  "Engineering": "#7c3aed",
  "Business Ops": "#059669",
  "Collaboration": "#0891b2",
  "Data & Analytics": "#ca8a04",
  "Web & Commerce": "#ea580c"
}

/* ─── HELPERS ────────────────────────────────────────────────────── */
const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n)
const fmtDate = (d) => {
  if (!d) return "—"
  const dt = new Date(d + "T00:00:00")
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
}
const daysUntil = (d) => {
  if (!d) return Infinity
  return Math.ceil((new Date(d + "T00:00:00") - new Date()) / 86400000)
}
const isOverdue = (lastReview, freq) => {
  if (!lastReview) return true
  const freqDays = { Monthly: 30, Quarterly: 90, "Semi-Annual": 180, Annual: 365 }
  return daysUntil(lastReview) < -(freqDays[freq] || 90)
}

// Convert snake_case DB rows to camelCase for the component
const toCamelCase = (obj) => {
  if (Array.isArray(obj)) return obj.map(toCamelCase)
  if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((acc, key) => {
      const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
      acc[camelKey] = toCamelCase(obj[key])
      return acc
    }, {})
  }
  return obj
}
const toSnakeCase = (obj) => {
  if (Array.isArray(obj)) return obj.map(toSnakeCase)
  if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((acc, key) => {
      const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
      acc[snakeKey] = toSnakeCase(obj[key])
      return acc
    }, {})
  }
  return obj
}

// Clean empty strings to null for DB date/nullable fields before saving
const DATE_FIELDS = ['renewal_date', 'last_review']
const NULLABLE_STRING_FIELDS = ['billing_frequency', 'data_residency', 'notes', 'purpose', 'owner', 'justification', 'approval_by', 'last_reviewed_by']
function cleanForDb(obj) {
  if (obj !== null && typeof obj === 'object' && !Array.isArray(obj)) {
    return Object.keys(obj).reduce((acc, key) => {
      let val = obj[key]
      // Convert empty/whitespace-only strings to null for date columns
      if (DATE_FIELDS.includes(key) && (val === '' || val === undefined || val === null || (typeof val === 'string' && val.trim() === ''))) val = null
      if (NULLABLE_STRING_FIELDS.includes(key) && (val === '' || val === undefined)) val = null
      acc[key] = val
      return acc
    }, {})
  }
  return obj
}

const fmtDateTime = (d) => {
  if (!d) return '—'
  const dt = new Date(d)
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
}

/* ─── EXPORT / IMPORT HELPERS ────────────────────────────────────── */
const PLATFORM_EXPORT_FIELDS = [
  { key: 'name', header: 'System Name' },
  { key: 'vendor', header: 'Vendor' },
  { key: 'category', header: 'Category' },
  { key: 'risk', header: 'Risk Level' },
  { key: 'sensitivity', header: 'Data Sensitivity' },
  { key: 'owner', header: 'System Owner' },
  { key: 'purpose', header: 'Business Purpose' },
  { key: 'authMethod', header: 'Auth Method' },
  { key: 'mfaEnforced', header: 'MFA Enforced' },
  { key: 'scimProvisioning', header: 'SCIM Provisioning' },
  { key: 'adminRoles', header: 'Admin Roles' },
  { key: 'productionImpact', header: 'Production Impact' },
  { key: 'internetFacing', header: 'Internet Facing' },
  { key: 'loggingEnabled', header: 'Logging Enabled' },
  { key: 'logRetention', header: 'Log Retention (days)' },
  { key: 'backupDr', header: 'Backup / DR' },
  { key: 'isoInScope', header: 'ISO In Scope' },
  { key: 'dataResidency', header: 'Data Residency' },
  { key: 'dataTypes', header: 'Data Types' },
  { key: 'cogs', header: 'Annual Spend (COGS)' },
  { key: 'isCogs', header: 'Is COGS' },
  { key: 'contractValue', header: 'Contract Value' },
  { key: 'billingFrequency', header: 'Billing Frequency' },
  { key: 'renewalDate', header: 'Renewal Date' },
  { key: 'lastReview', header: 'Last Review' },
  { key: 'reviewFrequency', header: 'Review Frequency' },
  { key: 'lastReviewedBy', header: 'Last Reviewed By' },
  { key: 'notes', header: 'Notes' },
]

const ACCESS_EXPORT_FIELDS = [
  { key: 'platformName', header: 'Platform' },
  { key: 'roleName', header: 'Role Name' },
  { key: 'userOrGroup', header: 'User / Group' },
  { key: 'accountType', header: 'Account Type' },
  { key: 'accessLevel', header: 'Access Level' },
  { key: 'justification', header: 'Business Justification' },
  { key: 'approvalBy', header: 'Approved By' },
  { key: 'mfaEnabled', header: 'MFA Enabled' },
  { key: 'breakGlass', header: 'Break Glass' },
  { key: 'lastReview', header: 'Last Review' },
  { key: 'reviewFrequency', header: 'Review Frequency' },
  { key: 'notes', header: 'Notes' },
]

function exportToExcel(data, fields, filename) {
  const rows = data.map(item => {
    const row = {}
    fields.forEach(f => {
      let val = item[f.key]
      if (Array.isArray(val)) val = val.join(', ')
      if (typeof val === 'boolean') val = val ? 'Yes' : 'No'
      row[f.header] = val ?? ''
    })
    return row
  })
  const ws = XLSX.utils.json_to_sheet(rows)
  // Auto-size columns
  const colWidths = fields.map(f => {
    const maxLen = Math.max(f.header.length, ...rows.map(r => String(r[f.header] || '').length))
    return { wch: Math.min(Math.max(maxLen + 2, 12), 50) }
  })
  ws['!cols'] = colWidths
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Data')
  XLSX.writeFile(wb, filename)
}

function parseExcelFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array' })
        const ws = wb.Sheets[wb.SheetNames[0]]
        const rows = XLSX.utils.sheet_to_json(ws)
        resolve(rows)
      } catch (err) { reject(err) }
    }
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsArrayBuffer(file)
  })
}

function mapImportedPlatformRow(row) {
  // Map from header names back to camelCase keys
  const headerMap = {}
  PLATFORM_EXPORT_FIELDS.forEach(f => { headerMap[f.header.toLowerCase()] = f.key })
  const mapped = {}
  Object.entries(row).forEach(([header, val]) => {
    const key = headerMap[header.toLowerCase()]
    if (!key) return
    if (['mfaEnforced','scimProvisioning','adminRoles','internetFacing','loggingEnabled','backupDr','isoInScope','isCogs'].includes(key)) {
      mapped[key] = typeof val === 'boolean' ? val : String(val).toLowerCase() === 'yes' || val === true || val === 'true'
    } else if (key === 'dataTypes') {
      mapped[key] = typeof val === 'string' ? val.split(',').map(s => s.trim()).filter(Boolean) : (Array.isArray(val) ? val : [])
    } else if (['cogs','contractValue','logRetention'].includes(key)) {
      mapped[key] = Number(val) || 0
    } else if (['renewalDate','lastReview'].includes(key)) {
      // Handle Excel date serial numbers, empty strings, and invalid dates
      if (val === '' || val === null || val === undefined || (typeof val === 'string' && val.trim() === '')) {
        mapped[key] = null
      } else if (typeof val === 'number') {
        const d = new Date((val - 25569) * 86400000)
        mapped[key] = d.toISOString().slice(0, 10)
      } else if (typeof val === 'string') {
        // Try to parse as date, null if invalid
        const parsed = new Date(val)
        mapped[key] = isNaN(parsed.getTime()) ? null : parsed.toISOString().slice(0, 10)
      } else {
        mapped[key] = null
      }
    } else {
      mapped[key] = val ?? ''
    }
  })
  return mapped
}

function mapImportedAccessRow(row, platforms) {
  const headerMap = {}
  ACCESS_EXPORT_FIELDS.forEach(f => { headerMap[f.header.toLowerCase()] = f.key })
  const mapped = {}
  Object.entries(row).forEach(([header, val]) => {
    const key = headerMap[header.toLowerCase()]
    if (!key) return
    if (['mfaEnabled','breakGlass'].includes(key)) {
      mapped[key] = typeof val === 'boolean' ? val : String(val).toLowerCase() === 'yes' || val === true || val === 'true'
    } else if (key === 'lastReview') {
      if (val === '' || val === null || val === undefined || (typeof val === 'string' && val.trim() === '')) {
        mapped[key] = null
      } else if (typeof val === 'number') {
        const d = new Date((val - 25569) * 86400000)
        mapped[key] = d.toISOString().slice(0, 10)
      } else if (typeof val === 'string') {
        const parsed = new Date(val)
        mapped[key] = isNaN(parsed.getTime()) ? null : parsed.toISOString().slice(0, 10)
      } else {
        mapped[key] = null
      }
    } else {
      mapped[key] = val ?? ''
    }
  })
  // Resolve platform name to ID
  if (mapped.platformName) {
    const plat = platforms.find(p => p.name.toLowerCase() === String(mapped.platformName).toLowerCase())
    if (plat) mapped.platformId = plat.id
    delete mapped.platformName
  }
  return mapped
}

/* ─── IMPORT MODAL ───────────────────────────────────────────────── */
function ImportModal({ isOpen, onClose, onImport, type, loading }) {
  const [file, setFile] = useState(null)
  const [mode, setMode] = useState('merge')
  const [preview, setPreview] = useState(null)
  const [error, setError] = useState('')
  const [results, setResults] = useState(null)

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0]
    if (!f) return
    setFile(f)
    setError('')
    setResults(null)
    try {
      const rows = await parseExcelFile(f)
      setPreview(rows)
    } catch {
      setError('Could not parse file. Make sure it is a valid .xlsx or .csv file.')
      setPreview(null)
    }
  }

  const handleImport = async () => {
    if (!preview || preview.length === 0) return
    const res = await onImport(preview, mode)
    if (res) setResults(res)
  }

  const handleClose = () => {
    setFile(null); setPreview(null); setError(''); setResults(null); setMode('merge')
    onClose()
  }

  if (!isOpen) return null

  // Show results screen after import
  if (results) {
    const hasErrors = results.errors && results.errors.length > 0
    return (
      <Modal isOpen={true} onClose={handleClose} title={`Import ${type} — Results`}>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-700">{results.inserted || 0}</div>
              <div className="text-xs text-green-600 font-medium mt-1">Inserted</div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-blue-700">{results.updated || 0}</div>
              <div className="text-xs text-blue-600 font-medium mt-1">Updated</div>
            </div>
            <div className={`border rounded-lg p-4 text-center ${results.failed > 0 ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
              <div className={`text-2xl font-bold ${results.failed > 0 ? 'text-red-700' : 'text-slate-400'}`}>{results.failed || 0}</div>
              <div className={`text-xs font-medium mt-1 ${results.failed > 0 ? 'text-red-600' : 'text-slate-400'}`}>Failed</div>
            </div>
          </div>

          {hasErrors && (
            <div className="border border-red-200 rounded-lg overflow-hidden">
              <div className="bg-red-50 px-4 py-2 text-xs font-semibold text-red-700 uppercase tracking-wider border-b border-red-200">Error Details</div>
              <div className="max-h-40 overflow-y-auto">
                {results.errors.map((err, i) => (
                  <div key={i} className="px-4 py-2 text-sm text-red-700 border-b border-red-100 last:border-0">
                    <span className="font-medium">Row {err.row}:</span> {err.message}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end pt-2 border-t border-slate-200">
            <button onClick={handleClose} className="px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700">Done</button>
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <Modal isOpen={true} onClose={handleClose} title={`Import ${type}`}>
      <div className="space-y-5">
        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-2">Select File</label>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 px-4 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer">
              <Upload className="w-4 h-4" />
              {file ? file.name : 'Choose .xlsx or .csv'}
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFileChange} className="hidden" />
            </label>
            {preview && <span className="text-sm text-green-600 font-medium">{preview.length} rows found</span>}
          </div>
          {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
        </div>

        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-3">Import Mode</label>
          <div className="space-y-2">
            <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${mode === 'merge' ? 'border-blue-400 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`} onClick={() => setMode('merge')}>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 flex-shrink-0 ${mode === 'merge' ? 'border-blue-600' : 'border-slate-300'}`}>
                {mode === 'merge' && <div className="w-2.5 h-2.5 rounded-full bg-blue-600" />}
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-900">Import & Merge</div>
                <div className="text-xs text-slate-500 mt-0.5">Match by name — update existing records, add new ones, keep unmatched</div>
              </div>
            </label>
            <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${mode === 'overwrite' ? 'border-red-400 bg-red-50' : 'border-slate-200 hover:bg-slate-50'}`} onClick={() => setMode('overwrite')}>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 flex-shrink-0 ${mode === 'overwrite' ? 'border-red-600' : 'border-slate-300'}`}>
                {mode === 'overwrite' && <div className="w-2.5 h-2.5 rounded-full bg-red-600" />}
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-900">Import & Overwrite</div>
                <div className="text-xs text-slate-500 mt-0.5">Delete all existing records and replace with imported data</div>
              </div>
            </label>
          </div>
        </div>

        {preview && preview.length > 0 && (
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-2">Preview (first 3 rows)</label>
            <div className="border border-slate-200 rounded-lg overflow-hidden overflow-x-auto max-h-48">
              <table className="w-full text-xs">
                <thead><tr className="bg-slate-50">
                  {Object.keys(preview[0]).map(h => <th key={h} className="px-2 py-1.5 text-left font-semibold text-slate-500 whitespace-nowrap border-b border-slate-200">{h}</th>)}
                </tr></thead>
                <tbody>
                  {preview.slice(0, 3).map((row, i) => <tr key={i} className="border-b border-slate-100">
                    {Object.values(row).map((v, j) => <td key={j} className="px-2 py-1.5 text-slate-700 whitespace-nowrap max-w-[200px] truncate">{String(v ?? '')}</td>)}
                  </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {mode === 'overwrite' && preview && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            This will permanently delete all existing {type.toLowerCase()} and replace them with {preview.length} imported rows.
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2 border-t border-slate-200">
          <button onClick={handleClose} disabled={loading} className="px-4 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
          <button
            onClick={handleImport}
            disabled={loading || !preview || preview.length === 0}
            className={`px-5 py-2.5 rounded-lg text-sm font-semibold text-white flex items-center gap-2 disabled:opacity-50 ${mode === 'overwrite' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Importing...</> : `Import & ${mode === 'overwrite' ? 'Overwrite' : 'Merge'}`}
          </button>
        </div>
      </div>
    </Modal>
  )
}

/* ─── REVIEW MODAL ───────────────────────────────────────────────── */
function ReviewModal({ isOpen, onClose, onSubmit, title, entityName, loading }) {
  const [notes, setNotes] = useState('')
  const handleSubmit = () => { onSubmit(notes); setNotes('') }
  if (!isOpen) return null
  return (
    <Modal isOpen={true} onClose={onClose} title={title || 'Confirm Review'}>
      <div className="space-y-4">
        <p className="text-sm text-slate-600">Mark <strong>{entityName}</strong> as reviewed. This will update the last review date to today and be logged in the audit trail.</p>
        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-2">Review Notes (optional)</label>
          <textarea className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px] resize-y" value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Access verified, no changes needed" />
        </div>
        <div className="flex justify-end gap-3 pt-2 border-t border-slate-200">
          <button onClick={onClose} disabled={loading} className="px-4 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
          <button onClick={handleSubmit} disabled={loading} className="px-5 py-2.5 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 flex items-center gap-2 disabled:opacity-50">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : <><ClipboardCheck className="w-4 h-4" /> Mark Reviewed</>}
          </button>
        </div>
      </div>
    </Modal>
  )
}

/* ─── REMOVE ACCESS MODAL ────────────────────────────────────────── */
function RemoveAccessModal({ isOpen, onClose, onSubmit, record, platformName, loading }) {
  const [reason, setReason] = useState('')
  if (!isOpen) return null
  return (
    <Modal isOpen={true} onClose={onClose} title="Remove User Access">
      <div className="space-y-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700">You are about to remove <strong>{record?.userOrGroup}</strong>'s <strong>{record?.accessLevel}</strong> access ({record?.roleName}) from <strong>{platformName}</strong>.</p>
          <p className="text-xs text-red-600 mt-1">This action will be logged in the audit trail.</p>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-2">Reason for Removal *</label>
          <textarea className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px] resize-y" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Employee departed, role change, access no longer needed" />
        </div>
        <div className="flex justify-end gap-3 pt-2 border-t border-slate-200">
          <button onClick={onClose} disabled={loading} className="px-4 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
          <button onClick={() => onSubmit(reason)} disabled={loading || !reason.trim()} className="px-5 py-2.5 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 flex items-center gap-2 disabled:opacity-50">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Removing...</> : <><UserMinus className="w-4 h-4" /> Remove Access</>}
          </button>
        </div>
      </div>
    </Modal>
  )
}

/* ─── REMOVE PLATFORM MODAL ─────────────────────────────────────── */
function RemovePlatformModal({ isOpen, onClose, onSubmit, platform, loading }) {
  const [reason, setReason] = useState('')
  if (!isOpen) return null
  return (
    <Modal isOpen={true} onClose={onClose} title="Remove Platform">
      <div className="space-y-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700">You are about to remove <strong>{platform?.name}</strong> ({platform?.vendor}) from the platform registry.</p>
          <p className="text-xs text-red-600 mt-1">All associated access records will also be removed. This action will be logged in the audit trail.</p>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-2">Reason for Removal *</label>
          <textarea className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px] resize-y" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Platform decommissioned, migrated to alternative, contract ended" />
        </div>
        <div className="flex justify-end gap-3 pt-2 border-t border-slate-200">
          <button onClick={onClose} disabled={loading} className="px-4 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
          <button onClick={() => onSubmit(reason)} disabled={loading || !reason.trim()} className="px-5 py-2.5 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 flex items-center gap-2 disabled:opacity-50">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Removing...</> : <><X className="w-4 h-4" /> Remove Platform</>}
          </button>
        </div>
      </div>
    </Modal>
  )
}

/* ─── AUDIT LOG PANEL ────────────────────────────────────────────── */
function AuditLogPanel({ logs, title }) {
  if (!logs || logs.length === 0) return null
  const actionColors = {
    reviewed: 'bg-green-50 border-green-200 text-green-700',
    removed: 'bg-red-50 border-red-200 text-red-700',
    added: 'bg-blue-50 border-blue-200 text-blue-700',
    modified: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    imported: 'bg-purple-50 border-purple-200 text-purple-700',
  }
  const typeLabels = { platform: 'Platform', access_record: 'Access' }
  const typeColors = { platform: 'bg-indigo-50 border-indigo-200 text-indigo-600', access_record: 'bg-cyan-50 border-cyan-200 text-cyan-600' }
  return (
    <div className="mt-6">
      <div className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-3 flex items-center gap-2"><History className="w-3.5 h-3.5" />{title || 'Audit Log'}</div>
      <div className="border border-slate-200 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
        {logs.map(log => (
          <div key={log.id} className="flex items-start gap-3 px-4 py-3 border-b border-slate-100 last:border-0">
            <Badge className={typeColors[log.recordType] || 'bg-slate-50 border-slate-200 text-slate-600'}>{typeLabels[log.recordType] || log.recordType}</Badge>
            <Badge className={actionColors[log.action] || 'bg-slate-50 border-slate-200 text-slate-600'}>{log.action}</Badge>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-slate-900">
                <span className="font-medium">{log.performedBy}</span>
                {log.action === 'removed' && log.snapshot && log.recordType === 'access_record' && <span className="text-slate-500"> removed <strong>{log.snapshot.user_or_group || log.snapshot.userOrGroup}</strong> ({log.snapshot.role_name || log.snapshot.roleName})</span>}
                {log.action === 'removed' && log.snapshot && log.recordType === 'platform' && <span className="text-slate-500"> removed <strong>{log.snapshot.name}</strong> ({log.snapshot.vendor})</span>}
                {log.action === 'reviewed' && <span className="text-slate-500"> completed review</span>}
              </div>
              {log.notes && <div className="text-xs text-slate-500 mt-0.5 italic">"{log.notes}"</div>}
              <div className="text-xs text-slate-400 mt-0.5">{fmtDateTime(log.performedAt)}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ─── TINY COMPONENTS ────────────────────────────────────────────── */
function Badge({ children, className }) {
  return (
    <span className={`px-2.5 py-0.5 rounded-md text-xs font-semibold uppercase tracking-wide border whitespace-nowrap inline-flex items-center ${className || 'bg-blue-50 border-blue-200 text-blue-600'}`}>
      {children}
    </span>
  )
}

function BoolDot({ value, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-slate-500">
      <span className={`w-2 h-2 rounded-full ${value ? 'bg-green-500' : 'bg-red-500'}`} />
      {label}
    </span>
  )
}

function MetricCard({ icon: Icon, label, value, sub, colorClass }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col gap-1.5">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClass || 'bg-blue-50 text-blue-600'}`}>
          <Icon className="w-5 h-5" />
        </div>
        <span className="text-sm text-slate-500 font-medium">{label}</span>
      </div>
      <div className="text-3xl font-bold text-slate-900 tracking-tight">{value}</div>
      {sub && <div className="text-sm text-slate-400">{sub}</div>}
    </div>
  )
}

function Modal({ isOpen, onClose, title, width, children }) {
  if (!isOpen) return null
  return (
    <div onClick={onClose} className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center pt-16 overflow-y-auto">
      <div onClick={e => e.stopPropagation()} className={`bg-white rounded-2xl shadow-2xl ${width || 'w-[720px]'} max-w-[94vw] max-h-[90vh] overflow-y-auto mb-10`}>
        <div className="flex justify-between items-center px-7 py-5 border-b border-slate-200 sticky top-0 bg-white z-10 rounded-t-2xl">
          <h2 className="text-lg font-bold text-slate-900">{title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-7 py-6">{children}</div>
      </div>
    </div>
  )
}

function FormField({ label, children, span }) {
  return (
    <div className={span === 3 ? 'col-span-3' : span === 2 ? 'col-span-2' : ''} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</label>
      {children}
    </div>
  )
}

/* ─── SUB-VIEW TABS ──────────────────────────────────────────────── */
function SubViewTabs({ activeView, setActiveView, overdueCount, overduePlatformCount, overdueAccessCount }) {
  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'platforms', label: 'Platform Registry', icon: Server },
    { id: 'access', label: 'Privileged Access', icon: Key },
  ]
  const badgeCounts = { platforms: overduePlatformCount, access: overdueAccessCount }
  return (
    <div className="flex gap-1 bg-white border border-slate-200 rounded-lg p-1 mb-6">
      {tabs.map(t => (
        <button
          key={t.id}
          onClick={() => setActiveView(t.id)}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
            activeView === t.id
              ? 'bg-blue-600 text-white shadow-sm'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <t.icon className="w-4 h-4" />
          {t.label}
          {badgeCounts[t.id] > 0 && (
            <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs font-bold bg-red-500 text-white">{badgeCounts[t.id]}</span>
          )}
        </button>
      ))}
    </div>
  )
}

/* ─── DASHBOARD ──────────────────────────────────────────────────── */
function Dashboard({ platforms, accessRecords, setActiveView }) {
  const critHigh = platforms.filter(p => p.risk === "Tier 1 - Critical" || p.risk === "Tier 1 - High").length
  const renewSoon = platforms.filter(p => { const d = daysUntil(p.renewalDate); return d >= 0 && d <= 90 })
  const overdueAccessReviews = accessRecords.filter(a => isOverdue(a.lastReview, a.reviewFrequency))
  const overduePlatformReviews = platforms.filter(p => isOverdue(p.lastReview, p.reviewFrequency || 'Quarterly'))
  const totalOverdueReviews = overdueAccessReviews.length + overduePlatformReviews.length
  const totalCogs = platforms.reduce((s, p) => s + (p.cogs || 0), 0)

  const riskData = RISK_LEVELS.map(r => ({ name: r, value: platforms.filter(p => p.risk === r).length })).filter(d => d.value > 0)
  const groupedCatData = Object.entries(CATEGORY_GROUPS).map(([group, categories]) => ({
    name: group, value: platforms.filter(p => categories.includes(p.category)).length
  })).filter(d => d.value > 0).sort((a, b) => b.value - a.value)
  const sensData = SENSITIVITY_LEVELS.map(s => ({ name: s, value: platforms.filter(p => p.sensitivity === s).length })).filter(d => d.value > 0)

  return (
    <div className="space-y-6">
      {/* Metrics */}
      <div className="grid grid-cols-4 gap-5">
        <MetricCard icon={Layers} label="Total Platforms" value={platforms.length} sub={`${fmt(totalCogs)} annual spend`} colorClass="bg-blue-50 text-blue-600" />
        <MetricCard icon={AlertTriangle} label="Critical / High Risk" value={critHigh} sub={`of ${platforms.length} platforms`} colorClass="bg-red-50 text-red-600" />
        <MetricCard icon={Calendar} label="Renewals ≤ 90d" value={renewSoon.length} sub={renewSoon.length > 0 ? `Next: ${fmtDate(renewSoon.sort((a, b) => daysUntil(a.renewalDate) - daysUntil(b.renewalDate))[0]?.renewalDate)}` : "None upcoming"} colorClass="bg-orange-50 text-orange-600" />
        <MetricCard icon={Clock} label="Overdue Reviews" value={totalOverdueReviews} sub={`${overduePlatformReviews.length} platforms · ${overdueAccessReviews.length} access records`} colorClass={totalOverdueReviews > 0 ? "bg-red-50 text-red-600" : "bg-green-50 text-green-600"} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-3 gap-5">
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2"><Shield className="w-4 h-4 text-blue-600" />Risk Distribution</h3>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart><Pie data={riskData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" stroke="none" paddingAngle={3}>
              {riskData.map((d, i) => <Cell key={i} fill={RISK_COLORS[d.name]} />)}
            </Pie><Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13 }} /></PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-3 justify-center mt-2">
            {riskData.map(d => <div key={d.name} className="flex items-center gap-1.5 text-xs text-slate-500">
              <span className="w-2 h-2 rounded-sm" style={{ background: RISK_COLORS[d.name] }} />{d.name.replace('Tier 1 - ', 'T1 ').replace('Tier 2 - ', 'T2 ').replace('Tier 3 - ', 'T3 ')} ({d.value})
            </div>)}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-blue-600" />By Category</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={groupedCatData} layout="vertical" margin={{ left: 0, right: 16 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" tick={{ fill: "#64748b", fontSize: 11 }} width={110} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13 }} formatter={(value, name, props) => [`${value} platforms`, props.payload.name]} labelFormatter={() => ''} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={18}>
                {groupedCatData.map((entry, i) => <Cell key={i} fill={GROUP_COLORS[entry.name] || "#2563eb"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2"><Lock className="w-4 h-4 text-blue-600" />Data Sensitivity</h3>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart><Pie data={sensData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" stroke="none" paddingAngle={3}>
              {sensData.map((d, i) => <Cell key={i} fill={SENSITIVITY_COLORS[d.name]} />)}
            </Pie><Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13 }} /></PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-3 justify-center mt-2">
            {sensData.map(d => <div key={d.name} className="flex items-center gap-1.5 text-xs text-slate-500">
              <span className="w-2 h-2 rounded-sm" style={{ background: SENSITIVITY_COLORS[d.name] }} />{d.name} ({d.value})
            </div>)}
          </div>
        </div>
      </div>

      {/* Bottom panels */}
      <div className="grid grid-cols-2 gap-5">
        {/* Upcoming Renewals */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2"><Calendar className="w-4 h-4 text-orange-500" />Upcoming Renewals</h3>
            <button onClick={() => setActiveView("platforms")} className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">View All <ChevronRight className="w-3 h-3" /></button>
          </div>
          {platforms.filter(p => daysUntil(p.renewalDate) >= 0).sort((a, b) => daysUntil(a.renewalDate) - daysUntil(b.renewalDate)).slice(0, 5).map(p => {
            const d = daysUntil(p.renewalDate)
            return (
              <div key={p.id} className="flex justify-between items-center py-3 border-b border-slate-100 last:border-0">
                <div>
                  <div className="text-sm font-semibold text-slate-900">{p.name}</div>
                  <div className="text-xs text-slate-400">{p.vendor} · {fmt(p.cogs)}/yr</div>
                </div>
                <div className="text-right">
                  <div className={`text-sm font-bold ${d <= 30 ? 'text-red-600' : 'text-orange-500'}`}>{d}d</div>
                  <div className="text-xs text-slate-400">{fmtDate(p.renewalDate)}</div>
                </div>
              </div>
            )
          })}
          {platforms.filter(p => daysUntil(p.renewalDate) >= 0).length === 0 && <p className="text-center text-sm text-slate-400 py-5">No upcoming renewals</p>}
        </div>

        {/* Overdue Reviews */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-500" />Overdue Reviews</h3>
          </div>
          {overduePlatformReviews.length > 0 && (
            <div className="mb-3">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center justify-between">
                <span>Platforms ({overduePlatformReviews.length})</span>
                <button onClick={() => setActiveView("platforms")} className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">View <ChevronRight className="w-3 h-3" /></button>
              </div>
              {overduePlatformReviews.slice(0, 3).map(p => (
                <div key={p.id} className="flex justify-between items-center py-2.5 border-b border-slate-100 last:border-0">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{p.name}</div>
                    <div className="text-xs text-slate-400">{p.vendor} · {p.category}</div>
                  </div>
                  <div className="text-right">
                    <Badge className="bg-red-50 border-red-200 text-red-600">{p.reviewFrequency || 'Quarterly'}</Badge>
                    <div className="text-xs text-slate-400 mt-1">Last: {p.lastReview ? fmtDate(p.lastReview) : 'Never'}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {overdueAccessReviews.length > 0 && (
            <div>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center justify-between">
                <span>Access Records ({overdueAccessReviews.length})</span>
                <button onClick={() => setActiveView("access")} className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">View <ChevronRight className="w-3 h-3" /></button>
              </div>
              {overdueAccessReviews.slice(0, 3).map(a => {
                const plat = platforms.find(p => p.id === a.platformId)
                return (
                  <div key={a.id} className="flex justify-between items-center py-2.5 border-b border-slate-100 last:border-0">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{a.roleName}</div>
                      <div className="text-xs text-slate-400">{a.userOrGroup} · {plat?.name || "Unknown"}</div>
                    </div>
                    <div className="text-right">
                      <Badge className="bg-red-50 border-red-200 text-red-600">{a.reviewFrequency}</Badge>
                      <div className="text-xs text-slate-400 mt-1">Last: {fmtDate(a.lastReview)}</div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
          {totalOverdueReviews === 0 && <p className="text-center text-sm text-green-600 py-5">✓ All reviews up to date</p>}
        </div>
      </div>
    </div>
  )
}

/* ─── PLATFORM DETAIL MODAL ──────────────────────────────────────── */
function PlatformDetail({ platform, onClose, accessRecords, auditLogs, onEdit }) {
  const p = platform
  const relatedAccess = accessRecords.filter(a => a.platformId === p.id)

  return (
    <Modal isOpen={true} onClose={onClose} title="" width="w-[860px]">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-xl font-bold text-slate-900">{p.name}</h2>
          <div className="text-sm text-slate-500">{p.vendor} · {p.category}</div>
        </div>
        <div className="flex gap-2 items-center">
          <Badge className={RISK_BG[p.risk]}>{p.risk}</Badge>
          <Badge className={SENSITIVITY_BG[p.sensitivity]}>{p.sensitivity} Sensitivity</Badge>
          {p.isCogs && <Badge className="bg-purple-50 border-purple-200 text-purple-600">COGS</Badge>}
          <button onClick={onEdit} className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-500"><Edit2 className="w-4 h-4" /></button>
        </div>
      </div>

      {/* General */}
      <div className="mb-6">
        <div className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-3 flex items-center gap-2"><Database className="w-3.5 h-3.5" />General Information</div>
        <div className="grid grid-cols-3 gap-x-6 gap-y-3">
          <div><div className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">System Owner</div><div className="text-sm font-medium text-slate-900">{p.owner || "—"}</div></div>
          <div><div className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Production Impact</div><div className="text-sm font-medium text-slate-900">{p.productionImpact}</div></div>
          <div><div className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Data Residency</div><div className="text-sm font-medium text-slate-900">{p.dataResidency || "—"}</div></div>
          <div className="col-span-3"><div className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Business Purpose</div><div className="text-sm font-medium text-slate-900">{p.purpose || "—"}</div></div>
          <div className="col-span-3"><div className="text-xs text-slate-400 uppercase tracking-wide mb-1">Data Types Stored</div>
            <div className="flex flex-wrap gap-1">{p.dataTypes?.length > 0 ? p.dataTypes.map(d => <Badge key={d} className="bg-blue-50 border-blue-200 text-blue-600">{d}</Badge>) : <span className="text-sm text-slate-400">None specified</span>}</div>
          </div>
        </div>
      </div>

      {/* Security */}
      <div className="mb-6">
        <div className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-3 flex items-center gap-2"><Shield className="w-3.5 h-3.5" />Security Controls</div>
        <div className="grid grid-cols-3 gap-x-6 gap-y-3">
          <div><div className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Auth Method</div><div className="text-sm font-medium text-slate-900">{p.authMethod}</div></div>
          <div><div className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Log Retention</div><div className="text-sm font-medium text-slate-900">{p.logRetention} days</div></div>
          <div><div className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">ISO In Scope</div><div className="text-sm font-medium text-slate-900">{p.isoInScope ? "Yes" : "No"}</div></div>
          <div className="flex flex-col gap-2 mt-1"><BoolDot value={p.mfaEnforced} label="MFA Enforced" /><BoolDot value={p.scimProvisioning} label="SCIM Provisioning" /></div>
          <div className="flex flex-col gap-2 mt-1"><BoolDot value={p.adminRoles} label="Admin Roles Exist" /><BoolDot value={p.loggingEnabled} label="Logging Enabled" /></div>
          <div className="flex flex-col gap-2 mt-1"><BoolDot value={p.internetFacing} label="Internet Facing" /><BoolDot value={p.backupDr} label="Backup / DR" /></div>
        </div>
      </div>

      {/* Contract */}
      <div className="mb-6">
        <div className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-3 flex items-center gap-2"><Calendar className="w-3.5 h-3.5" />Contract & Financials</div>
        <div className="grid grid-cols-3 gap-x-6 gap-y-3">
          <div><div className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Contract Value</div><div className="text-sm font-medium text-slate-900">{p.contractValue ? fmt(p.contractValue) : "—"}</div></div>
          <div><div className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Billing Frequency</div><div className="text-sm font-medium text-slate-900">{p.billingFrequency || "—"}</div></div>
          <div><div className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Annual Spend (COGS)</div><div className="text-sm font-medium text-slate-900">{p.cogs ? fmt(p.cogs) : "—"}</div></div>
          <div><div className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Renewal Date</div><div className="text-sm font-medium text-slate-900">{fmtDate(p.renewalDate)}{daysUntil(p.renewalDate) <= 90 && daysUntil(p.renewalDate) >= 0 ? ` (${daysUntil(p.renewalDate)}d)` : ""}</div></div>
          <div><div className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Is COGS Item</div><div className="text-sm font-medium text-slate-900">{p.isCogs ? "Yes" : "No"}</div></div>
        </div>
        {p.notes && <div className="mt-3"><div className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Notes</div><div className="text-sm text-slate-500 italic">{p.notes}</div></div>}
      </div>

      {/* Review Status */}
      <div className="mb-6">
        <div className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-3 flex items-center gap-2"><ClipboardCheck className="w-3.5 h-3.5" />Review Status</div>
        <div className="grid grid-cols-3 gap-x-6 gap-y-3">
          <div><div className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Last Reviewed</div><div className={`text-sm font-medium ${isOverdue(p.lastReview, p.reviewFrequency) ? 'text-red-600' : 'text-slate-900'}`}>{p.lastReview ? fmtDate(p.lastReview) : "Never"}{p.lastReview && isOverdue(p.lastReview, p.reviewFrequency) && ' — OVERDUE'}</div></div>
          <div><div className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Review Frequency</div><div className="text-sm font-medium text-slate-900">{p.reviewFrequency || "Quarterly"}</div></div>
          <div><div className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Last Reviewed By</div><div className="text-sm font-medium text-slate-900">{p.lastReviewedBy || "—"}</div></div>
        </div>
      </div>

      {/* Related Access */}
      {relatedAccess.length > 0 && (
        <div>
          <div className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-3 flex items-center gap-2"><Key className="w-3.5 h-3.5" />Privileged Access ({relatedAccess.length})</div>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50">
                  {["Role", "User / Group", "Level", "MFA", "Break Glass", "Review"].map(h => (
                    <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {relatedAccess.map(a => (
                  <tr key={a.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-3 py-2.5 font-medium text-slate-900">{a.roleName}</td>
                    <td className="px-3 py-2.5 text-slate-500">{a.userOrGroup}</td>
                    <td className="px-3 py-2.5"><Badge className={a.accessLevel === "Super Admin" ? "bg-red-50 border-red-200 text-red-600" : a.accessLevel === "Admin" ? "bg-orange-50 border-orange-200 text-orange-600" : "bg-blue-50 border-blue-200 text-blue-600"}>{a.accessLevel}</Badge></td>
                    <td className="px-3 py-2.5"><BoolDot value={a.mfaEnabled} label="" /></td>
                    <td className="px-3 py-2.5">{a.breakGlass ? <Badge className="bg-red-50 border-red-200 text-red-600">Break Glass</Badge> : "—"}</td>
                    <td className={`px-3 py-2.5 text-xs ${isOverdue(a.lastReview, a.reviewFrequency) ? 'text-red-600' : 'text-slate-500'}`}>{fmtDate(a.lastReview)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Audit Trail */}
      {auditLogs && (() => {
        const platLogs = auditLogs.filter(l => l.recordId === p.id || (l.recordType === 'access_record' && relatedAccess.some(a => a.id === l.recordId)))
        return platLogs.length > 0 ? <AuditLogPanel logs={platLogs.slice(0, 15)} title={`Audit Trail (${platLogs.length})`} /> : null
      })()}
    </Modal>
  )
}

/* ─── ACCESS DETAIL MODAL ───────────────────────────────────────── */
function AccessDetail({ record, onClose, platforms, auditLogs, onEdit, onReview, onRemove }) {
  const a = record
  const platform = platforms.find(p => p.id === a.platformId)
  const overdue = isOverdue(a.lastReview, a.reviewFrequency)

  return (
    <Modal isOpen={true} onClose={onClose} title="" width="w-[760px]">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-xl font-bold text-slate-900">{a.roleName}</h2>
          <div className="text-sm text-slate-500">{a.userOrGroup} · {platform?.name || 'Unknown Platform'}</div>
        </div>
        <div className="flex gap-2 items-center">
          <Badge className={a.accessLevel === "Super Admin" ? "bg-red-50 border-red-200 text-red-600" : a.accessLevel === "Admin" ? "bg-orange-50 border-orange-200 text-orange-600" : "bg-blue-50 border-blue-200 text-blue-600"}>{a.accessLevel}</Badge>
          <Badge className={a.accountType === "Emergency" ? "bg-red-50 border-red-200 text-red-600" : a.accountType === "API/Bot" ? "bg-purple-50 border-purple-200 text-purple-600" : "bg-blue-50 border-blue-200 text-blue-600"}>{a.accountType}</Badge>
          {a.breakGlass && <Badge className="bg-red-50 border-red-200 text-red-600">Break Glass</Badge>}
          <button onClick={onEdit} className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-500" title="Edit"><Edit2 className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Access Details */}
      <div className="mb-6">
        <div className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-3 flex items-center gap-2"><Key className="w-3.5 h-3.5" />Access Details</div>
        <div className="grid grid-cols-3 gap-x-6 gap-y-3">
          <div><div className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Platform</div><div className="text-sm font-medium text-slate-900">{platform?.name || 'Unknown'}</div><div className="text-xs text-slate-400">{platform?.vendor}</div></div>
          <div><div className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Role Name</div><div className="text-sm font-medium text-slate-900">{a.roleName}</div></div>
          <div><div className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">User / Group</div><div className="text-sm font-medium text-slate-900">{a.userOrGroup}</div></div>
          <div><div className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Access Level</div><div className="text-sm font-medium text-slate-900">{a.accessLevel}</div></div>
          <div><div className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Account Type</div><div className="text-sm font-medium text-slate-900">{a.accountType}</div></div>
          <div><div className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Approved By</div><div className="text-sm font-medium text-slate-900">{a.approvalBy || '—'}</div></div>
          <div className="col-span-3"><div className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Business Justification</div><div className="text-sm font-medium text-slate-900">{a.justification || '—'}</div></div>
        </div>
      </div>

      {/* Security Controls */}
      <div className="mb-6">
        <div className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-3 flex items-center gap-2"><Shield className="w-3.5 h-3.5" />Security Controls</div>
        <div className="grid grid-cols-3 gap-x-6 gap-y-3">
          <div className="flex flex-col gap-2"><BoolDot value={a.mfaEnabled} label="MFA Enabled" /></div>
          <div className="flex flex-col gap-2"><BoolDot value={a.breakGlass} label="Break Glass Account" /></div>
          <div><div className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Platform Auth</div><div className="text-sm font-medium text-slate-900">{platform?.authMethod || '—'}</div></div>
        </div>
      </div>

      {/* Review Status */}
      <div className="mb-6">
        <div className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-3 flex items-center gap-2"><ClipboardCheck className="w-3.5 h-3.5" />Review Status</div>
        <div className="grid grid-cols-3 gap-x-6 gap-y-3">
          <div><div className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Last Reviewed</div><div className={`text-sm font-medium ${overdue ? 'text-red-600' : 'text-slate-900'}`}>{a.lastReview ? fmtDate(a.lastReview) : 'Never'}{overdue && ' — OVERDUE'}</div></div>
          <div><div className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Review Frequency</div><div className="text-sm font-medium text-slate-900">{a.reviewFrequency || 'Quarterly'}</div></div>
          <div><div className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Platform Risk</div><div className="text-sm font-medium text-slate-900"><Badge className={RISK_BG[platform?.risk]}>{platform?.risk || '—'}</Badge></div></div>
        </div>
      </div>

      {/* Notes */}
      {a.notes && (
        <div className="mb-6">
          <div className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Notes</div>
          <div className="text-sm text-slate-500 italic">{a.notes}</div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2 mb-6 pt-4 border-t border-slate-200">
        <button onClick={onReview} className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700"><ClipboardCheck className="w-4 h-4" />Mark Reviewed</button>
        <button onClick={onRemove} className="flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700"><UserMinus className="w-4 h-4" />Remove Access</button>
      </div>

      {/* Audit Trail */}
      {auditLogs && (() => {
        const accessLogs = auditLogs.filter(l => l.recordId === a.id)
        return accessLogs.length > 0 ? <AuditLogPanel logs={accessLogs.slice(0, 15)} title={`Audit Trail (${accessLogs.length})`} /> : null
      })()}
    </Modal>
  )
}

/* ─── PLATFORM FORM MODAL ────────────────────────────────────────── */
function PlatformForm({ initial, onSave, onClose, saving }) {
  const [form, setForm] = useState(initial || {
    name: "", vendor: "", category: "", risk: "Tier 2 - Medium", purpose: "", cogs: 0,
    isCogs: false, contractValue: 0, billingFrequency: "Annual",
    dataTypes: [], sensitivity: "Medium", owner: "", authMethod: "SSO (SAML)",
    mfaEnforced: true, scimProvisioning: false, adminRoles: false,
    productionImpact: "Medium", internetFacing: true, loggingEnabled: true,
    logRetention: 90, backupDr: false, dataResidency: "", renewalDate: "",
    isoInScope: false, reviewFrequency: "Quarterly", notes: ""
  })
  const upd = (k, v) => setForm({ ...form, [k]: v })
  const toggleDataType = (dt) => {
    const arr = form.dataTypes || []
    upd("dataTypes", arr.includes(dt) ? arr.filter(x => x !== dt) : [...arr, dt])
  }
  const handleSave = () => {
    if (!form.name || !form.vendor) return
    onSave({ ...form, cogs: Number(form.cogs) || 0, contractValue: Number(form.contractValue) || 0, logRetention: Number(form.logRetention) || 0 })
  }

  const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
  const selectCls = inputCls + " cursor-pointer"

  return (
    <Modal isOpen={true} onClose={onClose} title={initial ? "Edit Platform" : "Add Platform"} width="w-[820px]">
      <div className="grid grid-cols-3 gap-x-6 gap-y-4">
        <FormField label="System Name *"><input className={inputCls} value={form.name} onChange={e => upd("name", e.target.value)} placeholder="e.g. Salesforce" /></FormField>
        <FormField label="Vendor *"><input className={inputCls} value={form.vendor} onChange={e => upd("vendor", e.target.value)} placeholder="e.g. Salesforce Inc." /></FormField>
        <FormField label="System Owner"><input className={inputCls} value={form.owner} onChange={e => upd("owner", e.target.value)} placeholder="e.g. IT, Finance" /></FormField>
        <FormField label="Category"><select className={selectCls} value={form.category} onChange={e => upd("category", e.target.value)}><option value="">Select...</option>{CATEGORIES.map(c => <option key={c}>{c}</option>)}</select></FormField>
        <FormField label="Risk Level"><select className={selectCls} value={form.risk} onChange={e => upd("risk", e.target.value)}>{RISK_LEVELS.map(r => <option key={r}>{r}</option>)}</select></FormField>
        <FormField label="Data Sensitivity"><select className={selectCls} value={form.sensitivity} onChange={e => upd("sensitivity", e.target.value)}>{SENSITIVITY_LEVELS.map(s => <option key={s}>{s}</option>)}</select></FormField>
        <FormField label="Business Purpose" span={3}><textarea className={inputCls + " min-h-[60px] resize-y"} value={form.purpose} onChange={e => upd("purpose", e.target.value)} /></FormField>

        <FormField label="Contract Value ($)"><input className={inputCls} type="number" value={form.contractValue} onChange={e => upd("contractValue", e.target.value)} /></FormField>
        <FormField label="Billing Frequency"><select className={selectCls} value={form.billingFrequency || ""} onChange={e => upd("billingFrequency", e.target.value)}><option value="">Select...</option>{BILLING_FREQUENCIES.map(b => <option key={b}>{b}</option>)}</select></FormField>
        <FormField label="Annual Spend (COGS)"><input className={inputCls} type="number" value={form.cogs} onChange={e => upd("cogs", e.target.value)} /></FormField>

        <FormField label="Data Types Stored" span={3}>
          <div className="flex flex-wrap gap-1.5">{DATA_TYPES.map(dt => {
            const sel = (form.dataTypes || []).includes(dt)
            return <button key={dt} type="button" onClick={() => toggleDataType(dt)} className={`px-3 py-1 rounded-md text-xs border transition-colors ${sel ? 'border-blue-400 bg-blue-50 text-blue-700 font-semibold' : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'}`}>{dt}</button>
          })}</div>
        </FormField>

        <FormField label="Auth Method"><select className={selectCls} value={form.authMethod} onChange={e => upd("authMethod", e.target.value)}>{AUTH_METHODS.map(a => <option key={a}>{a}</option>)}</select></FormField>
        <FormField label="Production Impact"><select className={selectCls} value={form.productionImpact} onChange={e => upd("productionImpact", e.target.value)}>{PRODUCTION_IMPACTS.map(p => <option key={p}>{p}</option>)}</select></FormField>
        <FormField label="Log Retention (days)"><input className={inputCls} type="number" value={form.logRetention} onChange={e => upd("logRetention", e.target.value)} /></FormField>
        <FormField label="Data Residency"><input className={inputCls} value={form.dataResidency} onChange={e => upd("dataResidency", e.target.value)} placeholder="e.g. US, EU" /></FormField>
        <FormField label="Renewal Date"><input className={inputCls} type="date" value={form.renewalDate} onChange={e => upd("renewalDate", e.target.value)} /></FormField>
        <FormField label="Review Frequency"><select className={selectCls} value={form.reviewFrequency || "Quarterly"} onChange={e => upd("reviewFrequency", e.target.value)}>{REVIEW_FREQUENCIES.map(f => <option key={f}>{f}</option>)}</select></FormField>

        {/* Boolean toggles */}
        <div className="col-span-3 grid grid-cols-4 gap-3 py-3">
          {[
            ["mfaEnforced", "MFA Enforced"], ["scimProvisioning", "SCIM Provisioning"], ["adminRoles", "Admin Roles Exist"], ["isCogs", "Is COGS Item"],
            ["internetFacing", "Internet Facing"], ["loggingEnabled", "Logging Enabled"], ["backupDr", "Backup / DR"], ["isoInScope", "ISO In Scope"]
          ].map(([k, label]) => (
            <div key={k} onClick={() => upd(k, !form[k])} className="flex items-center gap-2.5 cursor-pointer">
              <div className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-colors ${form[k] ? 'bg-blue-600 border-blue-600' : 'border-slate-300'}`}>
                {form[k] && <CheckCircle className="w-3.5 h-3.5 text-white" />}
              </div>
              <span className="text-sm text-slate-600">{label}</span>
            </div>
          ))}
        </div>

        <FormField label="Notes" span={3}><textarea className={inputCls + " min-h-[60px] resize-y"} value={form.notes} onChange={e => upd("notes", e.target.value)} /></FormField>
      </div>

      <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-200">
        <button onClick={onClose} disabled={saving} className="px-4 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
        <button onClick={handleSave} disabled={saving || !form.name || !form.vendor} className="px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : (initial ? "Save Changes" : "Add Platform")}
        </button>
      </div>
    </Modal>
  )
}

/* ─── ACCESS FORM MODAL ──────────────────────────────────────────── */
function AccessForm({ initial, platforms, onSave, onClose, saving }) {
  const [form, setForm] = useState(initial || {
    platformId: "", roleName: "", userOrGroup: "", accountType: "Named User",
    accessLevel: "Standard", justification: "", approvalBy: "", mfaEnabled: true,
    breakGlass: false, lastReview: "", reviewFrequency: "Quarterly", notes: ""
  })
  const upd = (k, v) => setForm({ ...form, [k]: v })
  const handleSave = () => {
    if (!form.platformId || !form.roleName || !form.userOrGroup) return
    onSave(form)
  }

  const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
  const selectCls = inputCls + " cursor-pointer"

  return (
    <Modal isOpen={true} onClose={onClose} title={initial ? "Edit Access Record" : "Add Access Record"}>
      <div className="grid grid-cols-2 gap-x-6 gap-y-4">
        <FormField label="Platform *"><select className={selectCls} value={form.platformId} onChange={e => upd("platformId", e.target.value)}><option value="">Select platform...</option>{platforms.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></FormField>
        <FormField label="Role Name *"><input className={inputCls} value={form.roleName} onChange={e => upd("roleName", e.target.value)} placeholder="e.g. Super Admin" /></FormField>
        <FormField label="User or Group *"><input className={inputCls} value={form.userOrGroup} onChange={e => upd("userOrGroup", e.target.value)} placeholder="e.g. Jane Smith" /></FormField>
        <FormField label="Account Type"><select className={selectCls} value={form.accountType} onChange={e => upd("accountType", e.target.value)}>{ACCOUNT_TYPES.map(a => <option key={a}>{a}</option>)}</select></FormField>
        <FormField label="Access Level"><select className={selectCls} value={form.accessLevel} onChange={e => upd("accessLevel", e.target.value)}>{ACCESS_LEVELS.map(a => <option key={a}>{a}</option>)}</select></FormField>
        <FormField label="Approval By"><input className={inputCls} value={form.approvalBy} onChange={e => upd("approvalBy", e.target.value)} /></FormField>
        <FormField label="Business Justification" span={2}><textarea className={inputCls + " min-h-[60px] resize-y"} value={form.justification} onChange={e => upd("justification", e.target.value)} /></FormField>
        <FormField label="Last Access Review"><input className={inputCls} type="date" value={form.lastReview} onChange={e => upd("lastReview", e.target.value)} /></FormField>
        <FormField label="Review Frequency"><select className={selectCls} value={form.reviewFrequency} onChange={e => upd("reviewFrequency", e.target.value)}>{REVIEW_FREQUENCIES.map(f => <option key={f}>{f}</option>)}</select></FormField>

        <div className="col-span-2 flex gap-6 py-2">
          {[["mfaEnabled", "MFA Enabled"], ["breakGlass", "Break Glass Account"]].map(([k, label]) => (
            <div key={k} onClick={() => upd(k, !form[k])} className="flex items-center gap-2.5 cursor-pointer">
              <div className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-colors ${form[k] ? 'bg-blue-600 border-blue-600' : 'border-slate-300'}`}>
                {form[k] && <CheckCircle className="w-3.5 h-3.5 text-white" />}
              </div>
              <span className="text-sm text-slate-600">{label}</span>
            </div>
          ))}
        </div>

        <FormField label="Notes" span={2}><textarea className={inputCls + " min-h-[60px] resize-y"} value={form.notes} onChange={e => upd("notes", e.target.value)} /></FormField>
      </div>

      <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-200">
        <button onClick={onClose} disabled={saving} className="px-4 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
        <button onClick={handleSave} disabled={saving || !form.platformId || !form.roleName || !form.userOrGroup} className="px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : (initial ? "Save Changes" : "Add Record")}
        </button>
      </div>
    </Modal>
  )
}

/* ─── PLATFORM REGISTRY ──────────────────────────────────────────── */
function PlatformRegistry({ platforms, onSave, onDelete, onBulkImport, onReview, onRemove, accessRecords, auditLogs, saving }) {
  const [search, setSearch] = useState("")
  const [filters, setFilters] = useState({ risk: "", category: "", sensitivity: "" })
  const [sortField, setSortField] = useState("name")
  const [sortDir, setSortDir] = useState("asc")
  const [showAdd, setShowAdd] = useState(false)
  const [editing, setEditing] = useState(null)
  const [viewing, setViewing] = useState(null)
  const [showFilters, setShowFilters] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [importing, setImporting] = useState(false)
  const [reviewing, setReviewing] = useState(null)
  const [reviewSaving, setReviewSaving] = useState(false)
  const [removing, setRemoving] = useState(null)
  const [removeSaving, setRemoveSaving] = useState(false)

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc")
    else { setSortField(field); setSortDir("asc") }
  }

  const filtered = useMemo(() => {
    let result = platforms.filter(p => {
      if (search && !`${p.name} ${p.vendor} ${p.owner} ${p.category}`.toLowerCase().includes(search.toLowerCase())) return false
      if (filters.risk && p.risk !== filters.risk) return false
      if (filters.category && p.category !== filters.category) return false
      if (filters.sensitivity && p.sensitivity !== filters.sensitivity) return false
      return true
    })
    result.sort((a, b) => {
      let va = a[sortField], vb = b[sortField]
      if (sortField === "cogs") { va = Number(va); vb = Number(vb) }
      else if (sortField === "risk") { const o = { "Tier 1 - Critical": 0, "Tier 1 - High": 1, "Tier 2 - Medium": 2, "Tier 3 - Low": 3 }; va = o[va] ?? 99; vb = o[vb] ?? 99 }
      else { va = String(va || "").toLowerCase(); vb = String(vb || "").toLowerCase() }
      if (va < vb) return sortDir === "asc" ? -1 : 1
      if (va > vb) return sortDir === "asc" ? 1 : -1
      return 0
    })
    return result
  }, [platforms, search, filters, sortField, sortDir])

  const handleSave = async (p) => { await onSave(p, !!editing); setShowAdd(false); setEditing(null) }
  const activeFilterCount = Object.values(filters).filter(Boolean).length

  const handleExport = () => {
    exportToExcel(platforms, PLATFORM_EXPORT_FIELDS, `platform-registry-${new Date().toISOString().slice(0,10)}.xlsx`)
  }

  const handleImport = async (rows, mode) => {
    setImporting(true)
    try {
      const mapped = rows.map(r => mapImportedPlatformRow(r)).filter(r => r.name && r.vendor)
      if (mapped.length === 0) return { inserted: 0, updated: 0, failed: rows.length, errors: [{ row: '-', message: 'No valid rows found. Ensure "System Name" and "Vendor" columns exist.' }] }
      const results = await onBulkImport(mapped, mode, 'platforms')
      return results
    } catch (err) {
      return { inserted: 0, updated: 0, failed: rows.length, errors: [{ row: '-', message: err.message }] }
    } finally {
      setImporting(false)
    }
  }

  const handleReview = async (notes) => {
    setReviewSaving(true)
    try {
      await onReview(reviewing, notes)
      setReviewing(null)
    } catch (err) {
      alert('Review failed: ' + err.message)
    } finally {
      setReviewSaving(false)
    }
  }

  const handleRemove = async (reason) => {
    setRemoveSaving(true)
    try {
      await onRemove(removing, reason)
      setRemoving(null)
    } catch (err) {
      alert('Remove failed: ' + err.message)
    } finally {
      setRemoveSaving(false)
    }
  }

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 opacity-30" />
    return sortDir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
  }

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">{filtered.length} of {platforms.length} platforms</p>
        <div className="flex gap-2">
          <button onClick={handleExport} className="flex items-center gap-2 px-3 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50"><Download className="w-4 h-4" />Export</button>
          <button onClick={() => setShowImport(true)} className="flex items-center gap-2 px-3 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50"><Upload className="w-4 h-4" />Import</button>
          <button onClick={() => setShowAdd(true)} className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700"><Plus className="w-4 h-4" />Add Platform</button>
        </div>
      </div>

      <div className="flex gap-3 items-center">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className="w-full bg-white border border-slate-200 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Search platforms, vendors, owners..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <button onClick={() => setShowFilters(!showFilters)} className="relative flex items-center gap-2 px-4 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50">
          <Filter className="w-4 h-4" />Filters
          {activeFilterCount > 0 && <span className="absolute -top-1.5 -right-1.5 w-4.5 h-4.5 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">{activeFilterCount}</span>}
        </button>
      </div>

      {showFilters && (
        <div className="flex gap-3 p-4 bg-white border border-slate-200 rounded-xl">
          <div className="flex-1"><label className="block text-xs text-slate-500 uppercase tracking-wider mb-1">Risk Level</label><select className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm" value={filters.risk} onChange={e => setFilters({ ...filters, risk: e.target.value })}><option value="">All</option>{RISK_LEVELS.map(r => <option key={r}>{r}</option>)}</select></div>
          <div className="flex-1"><label className="block text-xs text-slate-500 uppercase tracking-wider mb-1">Category</label><select className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm" value={filters.category} onChange={e => setFilters({ ...filters, category: e.target.value })}><option value="">All</option>{CATEGORIES.map(c => <option key={c}>{c}</option>)}</select></div>
          <div className="flex-1"><label className="block text-xs text-slate-500 uppercase tracking-wider mb-1">Sensitivity</label><select className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm" value={filters.sensitivity} onChange={e => setFilters({ ...filters, sensitivity: e.target.value })}><option value="">All</option>{SENSITIVITY_LEVELS.map(s => <option key={s}>{s}</option>)}</select></div>
          <div className="flex items-end"><button onClick={() => setFilters({ risk: "", category: "", sensitivity: "" })} className="px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">Clear</button></div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50">
                {[["name","System"],["category","Category"],["risk","Risk"],["sensitivity","Sensitivity"],["owner","Owner"],["cogs","COGS"],["","Controls"],["renewalDate","Renewal"],["lastReview","Last Review"],["","Actions"]].map(([field, label]) => (
                  <th key={label} onClick={field ? () => toggleSort(field) : undefined} className={`px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200 whitespace-nowrap ${field ? 'cursor-pointer select-none' : ''}`}>
                    <span className="flex items-center gap-1">{label} {field && <SortIcon field={field} />}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => {
                const renewDays = daysUntil(p.renewalDate)
                return (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="px-3 py-3"><div className="font-semibold text-slate-900">{p.name}</div><div className="text-xs text-slate-400">{p.vendor}</div></td>
                    <td className="px-3 py-3 text-slate-500">{p.category}</td>
                    <td className="px-3 py-3"><Badge className={RISK_BG[p.risk]}>{p.risk}</Badge></td>
                    <td className="px-3 py-3"><Badge className={SENSITIVITY_BG[p.sensitivity]}>{p.sensitivity}</Badge></td>
                    <td className="px-3 py-3 text-slate-500">{p.owner}</td>
                    <td className="px-3 py-3 text-slate-900 text-xs font-medium">{fmt(p.cogs)}</td>
                    <td className="px-3 py-3">
                      <div className="flex gap-1">
                        <span title="MFA" style={{ opacity: p.mfaEnforced ? 1 : 0.3 }}>🔐</span>
                        <span title="SSO" style={{ opacity: p.authMethod?.startsWith("SSO") ? 1 : 0.3 }}>🔑</span>
                        <span title="Logging" style={{ opacity: p.loggingEnabled ? 1 : 0.3 }}>📋</span>
                        <span title="Backup" style={{ opacity: p.backupDr ? 1 : 0.3 }}>💾</span>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className={`text-xs ${renewDays <= 30 ? 'text-red-600' : renewDays <= 90 ? 'text-orange-500' : 'text-slate-500'}`}>{fmtDate(p.renewalDate)}</div>
                      {renewDays >= 0 && renewDays <= 90 && <div className={`text-[10px] ${renewDays <= 30 ? 'text-red-600' : 'text-orange-500'}`}>{renewDays}d remaining</div>}
                    </td>
                    <td className={`px-3 py-3 text-xs ${isOverdue(p.lastReview, p.reviewFrequency) ? 'text-red-600 font-medium' : 'text-slate-500'}`}>
                      {p.lastReview ? fmtDate(p.lastReview) : <span className="text-slate-400">Never</span>}
                      {p.lastReview && isOverdue(p.lastReview, p.reviewFrequency) && <div className="text-[10px]">OVERDUE</div>}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex gap-1">
                        <button onClick={() => setViewing(p)} className="p-1.5 border border-slate-200 rounded-md hover:bg-slate-50 text-slate-500" title="View"><Eye className="w-3.5 h-3.5" /></button>
                        <button onClick={() => setEditing(p)} className="p-1.5 border border-slate-200 rounded-md hover:bg-slate-50 text-slate-500" title="Edit"><Edit2 className="w-3.5 h-3.5" /></button>
                        <button onClick={() => setReviewing(p)} className="p-1.5 border border-green-200 rounded-md hover:bg-green-50 text-green-600" title="Mark Reviewed"><ClipboardCheck className="w-3.5 h-3.5" /></button>
                        <button onClick={() => setRemoving(p)} className="p-1.5 border border-red-200 rounded-md hover:bg-red-50 text-red-500" title="Remove Platform"><X className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                )
              })}
              {filtered.length === 0 && <tr><td colSpan={10} className="text-center py-10 text-slate-400">No platforms match your search or filters.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {showAdd && <PlatformForm onSave={handleSave} onClose={() => setShowAdd(false)} saving={saving} />}
      {editing && <PlatformForm initial={editing} onSave={handleSave} onClose={() => setEditing(null)} saving={saving} />}
      {viewing && <PlatformDetail platform={viewing} onClose={() => setViewing(null)} accessRecords={accessRecords} auditLogs={auditLogs} onEdit={() => { setEditing(viewing); setViewing(null) }} />}
      <ImportModal isOpen={showImport} onClose={() => setShowImport(false)} onImport={handleImport} type="Platforms" loading={importing} />
      <ReviewModal isOpen={!!reviewing} onClose={() => setReviewing(null)} onSubmit={handleReview} title="Review Platform" entityName={reviewing?.name || ''} loading={reviewSaving} />
      <RemovePlatformModal isOpen={!!removing} onClose={() => setRemoving(null)} onSubmit={handleRemove} platform={removing} loading={removeSaving} />

      {/* Recent Audit Activity */}
      {auditLogs && auditLogs.filter(l => l.recordType === 'platform').length > 0 && (
        <AuditLogPanel logs={auditLogs.filter(l => l.recordType === 'platform').slice(0, 20)} title="Recent Platform Audit Activity" />
      )}
    </div>
  )
}

/* ─── ACCESS REGISTER ────────────────────────────────────────────── */
function AccessRegister({ accessRecords, onSave, onDelete, onBulkImport, onReview, onRemove, platforms, auditLogs, saving }) {
  const [search, setSearch] = useState("")
  const [filters, setFilters] = useState({ level: "", accountType: "", platformId: "", overdueOnly: false })
  const [sortField, setSortField] = useState("roleName")
  const [sortDir, setSortDir] = useState("asc")
  const [showAdd, setShowAdd] = useState(false)
  const [editing, setEditing] = useState(null)
  const [viewing, setViewing] = useState(null)
  const [showFilters, setShowFilters] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [importing, setImporting] = useState(false)
  const [reviewing, setReviewing] = useState(null)
  const [reviewSaving, setReviewSaving] = useState(false)
  const [removing, setRemoving] = useState(null)
  const [removeSaving, setRemoveSaving] = useState(false)

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc")
    else { setSortField(field); setSortDir("asc") }
  }

  const filtered = useMemo(() => {
    let result = accessRecords.filter(a => {
      const plat = platforms.find(p => p.id === a.platformId)
      if (search && !`${a.roleName} ${a.userOrGroup} ${a.approvalBy} ${plat?.name || ""}`.toLowerCase().includes(search.toLowerCase())) return false
      if (filters.level && a.accessLevel !== filters.level) return false
      if (filters.accountType && a.accountType !== filters.accountType) return false
      if (filters.platformId && a.platformId !== filters.platformId) return false
      if (filters.overdueOnly && !isOverdue(a.lastReview, a.reviewFrequency)) return false
      return true
    })
    result.sort((a, b) => {
      let va, vb
      if (sortField === "platform") {
        va = (platforms.find(p => p.id === a.platformId)?.name || "").toLowerCase()
        vb = (platforms.find(p => p.id === b.platformId)?.name || "").toLowerCase()
      } else {
        va = String(a[sortField] || "").toLowerCase()
        vb = String(b[sortField] || "").toLowerCase()
      }
      if (va < vb) return sortDir === "asc" ? -1 : 1
      if (va > vb) return sortDir === "asc" ? 1 : -1
      return 0
    })
    return result
  }, [accessRecords, platforms, search, filters, sortField, sortDir])

  const handleSave = async (a) => { await onSave(a, !!editing); setShowAdd(false); setEditing(null) }
  const activeFilterCount = Object.values(filters).filter(v => v === true || (typeof v === "string" && v !== "")).length

  const handleReview = async (notes) => {
    setReviewSaving(true)
    try {
      await onReview(reviewing, notes)
      setReviewing(null)
    } catch (err) {
      alert('Review failed: ' + err.message)
    } finally {
      setReviewSaving(false)
    }
  }

  const handleRemove = async (reason) => {
    setRemoveSaving(true)
    try {
      await onRemove(removing, reason)
      setRemoving(null)
    } catch (err) {
      alert('Remove failed: ' + err.message)
    } finally {
      setRemoveSaving(false)
    }
  }

  const handleExport = () => {
    const enriched = accessRecords.map(a => {
      const plat = platforms.find(p => p.id === a.platformId)
      return { ...a, platformName: plat?.name || '' }
    })
    exportToExcel(enriched, ACCESS_EXPORT_FIELDS, `privileged-access-${new Date().toISOString().slice(0,10)}.xlsx`)
  }

  const handleImport = async (rows, mode) => {
    setImporting(true)
    try {
      const mapped = rows.map(r => mapImportedAccessRow(r, platforms)).filter(r => r.platformId && r.roleName && r.userOrGroup)
      if (mapped.length === 0) return { inserted: 0, updated: 0, failed: rows.length, errors: [{ row: '-', message: 'No valid rows. Ensure "Platform" column matches existing platform names.' }] }
      const results = await onBulkImport(mapped, mode, 'access_records')
      return results
    } catch (err) {
      return { inserted: 0, updated: 0, failed: rows.length, errors: [{ row: '-', message: err.message }] }
    } finally {
      setImporting(false)
    }
  }

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 opacity-30" />
    return sortDir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
  }

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">{filtered.length} of {accessRecords.length} access records</p>
        <div className="flex gap-2">
          <button onClick={handleExport} className="flex items-center gap-2 px-3 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50"><Download className="w-4 h-4" />Export</button>
          <button onClick={() => setShowImport(true)} className="flex items-center gap-2 px-3 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50"><Upload className="w-4 h-4" />Import</button>
          <button onClick={() => setShowAdd(true)} className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700"><Plus className="w-4 h-4" />Add Record</button>
        </div>
      </div>

      <div className="flex gap-3 items-center">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className="w-full bg-white border border-slate-200 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Search roles, users, platforms..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <button onClick={() => setShowFilters(!showFilters)} className="relative flex items-center gap-2 px-4 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50">
          <Filter className="w-4 h-4" />Filters
          {activeFilterCount > 0 && <span className="absolute -top-1.5 -right-1.5 w-4.5 h-4.5 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">{activeFilterCount}</span>}
        </button>
      </div>

      {showFilters && (
        <div className="flex gap-3 p-4 bg-white border border-slate-200 rounded-xl flex-wrap">
          <div className="flex-1 min-w-[160px]"><label className="block text-xs text-slate-500 uppercase tracking-wider mb-1">Platform</label><select className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm" value={filters.platformId} onChange={e => setFilters({ ...filters, platformId: e.target.value })}><option value="">All</option>{platforms.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
          <div className="flex-1 min-w-[140px]"><label className="block text-xs text-slate-500 uppercase tracking-wider mb-1">Access Level</label><select className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm" value={filters.level} onChange={e => setFilters({ ...filters, level: e.target.value })}><option value="">All</option>{ACCESS_LEVELS.map(l => <option key={l}>{l}</option>)}</select></div>
          <div className="flex-1 min-w-[140px]"><label className="block text-xs text-slate-500 uppercase tracking-wider mb-1">Account Type</label><select className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm" value={filters.accountType} onChange={e => setFilters({ ...filters, accountType: e.target.value })}><option value="">All</option>{ACCOUNT_TYPES.map(a => <option key={a}>{a}</option>)}</select></div>
          <div className="flex items-end gap-2">
            <button onClick={() => setFilters({ ...filters, overdueOnly: !filters.overdueOnly })} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border ${filters.overdueOnly ? 'bg-red-600 border-red-600 text-white' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
              <AlertTriangle className="w-3.5 h-3.5" />Overdue
            </button>
            <button onClick={() => setFilters({ level: "", accountType: "", platformId: "", overdueOnly: false })} className="px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">Clear</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50">
                {[["platform","Platform"],["roleName","Role"],["userOrGroup","User / Group"],["","Type"],["","Level"],["","MFA"],["","Flags"],["lastReview","Last Review"],["","Frequency"],["","Actions"]].map(([field, label]) => (
                  <th key={label} onClick={field ? () => toggleSort(field) : undefined} className={`px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200 whitespace-nowrap ${field ? 'cursor-pointer select-none' : ''}`}>
                    <span className="flex items-center gap-1">{label} {field && <SortIcon field={field} />}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(a => {
                const plat = platforms.find(p => p.id === a.platformId)
                const overdue = isOverdue(a.lastReview, a.reviewFrequency)
                return (
                  <tr key={a.id} className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${overdue ? 'bg-red-50/50' : ''}`}>
                    <td className="px-3 py-3"><div className="font-semibold text-slate-900">{plat?.name || "Unknown"}</div><div className="text-xs text-slate-400">{plat?.vendor}</div></td>
                    <td className="px-3 py-3 font-medium text-slate-900">{a.roleName}</td>
                    <td className="px-3 py-3 text-slate-500">{a.userOrGroup}</td>
                    <td className="px-3 py-3"><Badge className={a.accountType === "Emergency" ? "bg-red-50 border-red-200 text-red-600" : a.accountType === "API/Bot" ? "bg-purple-50 border-purple-200 text-purple-600" : "bg-blue-50 border-blue-200 text-blue-600"}>{a.accountType}</Badge></td>
                    <td className="px-3 py-3"><Badge className={a.accessLevel === "Super Admin" ? "bg-red-50 border-red-200 text-red-600" : a.accessLevel === "Admin" ? "bg-orange-50 border-orange-200 text-orange-600" : "bg-blue-50 border-blue-200 text-blue-600"}>{a.accessLevel}</Badge></td>
                    <td className="px-3 py-3"><BoolDot value={a.mfaEnabled} label="" /></td>
                    <td className="px-3 py-3">
                      <div className="flex gap-1">
                        {a.breakGlass && <Badge className="bg-red-50 border-red-200 text-red-600">BG</Badge>}
                        {overdue && <Badge className="bg-red-50 border-red-200 text-red-600">OVERDUE</Badge>}
                      </div>
                    </td>
                    <td className={`px-3 py-3 text-xs ${overdue ? 'text-red-600' : 'text-slate-500'}`}>{fmtDate(a.lastReview)}</td>
                    <td className="px-3 py-3 text-xs text-slate-500">{a.reviewFrequency}</td>
                    <td className="px-3 py-3">
                      <div className="flex gap-1">
                        <button onClick={() => setViewing(a)} className="p-1.5 border border-slate-200 rounded-md hover:bg-slate-50 text-slate-500" title="View"><Eye className="w-3.5 h-3.5" /></button>
                        <button onClick={() => setEditing(a)} className="p-1.5 border border-slate-200 rounded-md hover:bg-slate-50 text-slate-500" title="Edit"><Edit2 className="w-3.5 h-3.5" /></button>
                        <button onClick={() => setReviewing(a)} className="p-1.5 border border-green-200 rounded-md hover:bg-green-50 text-green-600" title="Mark Reviewed"><ClipboardCheck className="w-3.5 h-3.5" /></button>
                        <button onClick={() => setRemoving(a)} className="p-1.5 border border-red-200 rounded-md hover:bg-red-50 text-red-500" title="Remove Access"><UserMinus className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                )
              })}
              {filtered.length === 0 && <tr><td colSpan={10} className="text-center py-10 text-slate-400">No access records match your search or filters.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {showAdd && <AccessForm platforms={platforms} onSave={handleSave} onClose={() => setShowAdd(false)} saving={saving} />}
      {editing && <AccessForm initial={editing} platforms={platforms} onSave={handleSave} onClose={() => setEditing(null)} saving={saving} />}
      {viewing && <AccessDetail record={viewing} onClose={() => setViewing(null)} platforms={platforms} auditLogs={auditLogs} onEdit={() => { setEditing(viewing); setViewing(null) }} onReview={() => { setReviewing(viewing); setViewing(null) }} onRemove={() => { setRemoving(viewing); setViewing(null) }} />}
      <ImportModal isOpen={showImport} onClose={() => setShowImport(false)} onImport={handleImport} type="Access Records" loading={importing} />
      <ReviewModal isOpen={!!reviewing} onClose={() => setReviewing(null)} onSubmit={handleReview} title="Review Access" entityName={reviewing ? `${reviewing.userOrGroup} — ${reviewing.roleName}` : ''} loading={reviewSaving} />
      <RemoveAccessModal isOpen={!!removing} onClose={() => setRemoving(null)} onSubmit={handleRemove} record={removing} platformName={removing ? (platforms.find(p => p.id === removing.platformId)?.name || 'Unknown') : ''} loading={removeSaving} />

      {/* Recent Audit Activity */}
      {auditLogs && auditLogs.filter(l => l.recordType === 'access_record').length > 0 && (
        <AuditLogPanel logs={auditLogs.filter(l => l.recordType === 'access_record').slice(0, 20)} title="Recent Access Audit Activity" />
      )}
    </div>
  )
}

/* ─── MAIN EXPORT: SaaSGovernanceView ────────────────────────────── */
export default function SaaSGovernanceView() {
  const { user } = useAuth()
  const { currentTenant } = useTenant()
  const [activeView, setActiveView] = useState("dashboard")
  const [platforms, setPlatforms] = useState([])
  const [accessRecords, setAccessRecords] = useState([])
  const [auditLogs, setAuditLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (currentTenant?.id) fetchData()
  }, [currentTenant?.id])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)

      const { data: platformsData, error: pErr } = await supabase
        .from('platforms')
        .select('*')
        .eq('tenant_id', currentTenant.id)
        .order('name')

      if (pErr) throw pErr

      const { data: accessData, error: aErr } = await supabase
        .from('access_records')
        .select('*')
        .eq('tenant_id', currentTenant.id)
        .order('role_name')

      if (aErr) throw aErr

      // Fetch audit logs
      const { data: logsData, error: lErr } = await supabase
        .from('audit_logs')
        .select('*')
        .eq('tenant_id', currentTenant.id)
        .order('performed_at', { ascending: false })
        .limit(500)

      if (lErr) {
        console.warn('Audit logs table may not exist yet:', lErr.message)
      }

      setPlatforms(toCamelCase(platformsData) || [])
      setAccessRecords(toCamelCase(accessData) || [])
      setAuditLogs(toCamelCase(logsData) || [])
    } catch (err) {
      console.error('Error fetching SaaS governance data:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const savePlatform = async (platform, isEdit) => {
    try {
      setSaving(true)
      const snakeData = cleanForDb(toSnakeCase(platform))
      const { id, created_at, updated_at, created_by, ...dataToSave } = snakeData

      // Always set tenant_id and created_by
      dataToSave.tenant_id = currentTenant.id
      if (!isEdit) dataToSave.created_by = user.id

      if (isEdit) {
        const { data, error } = await supabase.from('platforms').update(dataToSave).eq('id', platform.id).select().single()
        if (error) throw error
        setPlatforms(prev => prev.map(p => p.id === platform.id ? toCamelCase(data) : p))
      } else {
        const { data, error } = await supabase.from('platforms').insert(dataToSave).select().single()
        if (error) throw error
        setPlatforms(prev => [...prev, toCamelCase(data)])
      }
    } catch (err) {
      console.error('Error saving platform:', err)
      alert('Error saving platform: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const deletePlatform = async (id) => {
    try {
      const { error } = await supabase.from('platforms').delete().eq('id', id)
      if (error) throw error
      setPlatforms(prev => prev.filter(p => p.id !== id))
    } catch (err) {
      console.error('Error deleting platform:', err)
      alert('Error deleting platform: ' + err.message)
    }
  }

  const saveAccessRecord = async (record, isEdit) => {
    try {
      setSaving(true)
      const snakeData = cleanForDb(toSnakeCase(record))
      const { id, created_at, updated_at, created_by, ...dataToSave } = snakeData

      dataToSave.tenant_id = currentTenant.id
      if (!isEdit) dataToSave.created_by = user.id

      if (isEdit) {
        const { data, error } = await supabase.from('access_records').update(dataToSave).eq('id', record.id).select().single()
        if (error) throw error
        setAccessRecords(prev => prev.map(a => a.id === record.id ? toCamelCase(data) : a))
      } else {
        const { data, error } = await supabase.from('access_records').insert(dataToSave).select().single()
        if (error) throw error
        setAccessRecords(prev => [...prev, toCamelCase(data)])
      }
    } catch (err) {
      console.error('Error saving access record:', err)
      alert('Error saving access record: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const deleteAccessRecord = async (id) => {
    try {
      const { error } = await supabase.from('access_records').delete().eq('id', id)
      if (error) throw error
      setAccessRecords(prev => prev.filter(a => a.id !== id))
    } catch (err) {
      console.error('Error deleting access record:', err)
      alert('Error deleting access record: ' + err.message)
    }
  }

  // Helper to write an audit log entry
  const writeAuditLog = async ({ recordType, recordId, action, notes, snapshot }) => {
    try {
      const { data, error } = await supabase.from('audit_logs').insert({
        tenant_id: currentTenant.id,
        record_type: recordType,
        record_id: recordId,
        action,
        performed_by: user.email || user.id,
        notes: notes || null,
        snapshot: snapshot || null,
      }).select().single()
      if (error) throw error
      setAuditLogs(prev => [toCamelCase(data), ...prev])
    } catch (err) {
      console.warn('Failed to write audit log:', err.message)
    }
  }

  // Review a platform (updates last_review date + creates audit entry)
  const reviewPlatform = async (platform, notes) => {
    try {
      setSaving(true)
      const today = new Date().toISOString().slice(0, 10)
      const { data, error } = await supabase.from('platforms')
        .update({ last_review: today, last_reviewed_by: user.email || user.id })
        .eq('id', platform.id).select().single()
      if (error) throw error
      setPlatforms(prev => prev.map(p => p.id === platform.id ? toCamelCase(data) : p))
      await writeAuditLog({ recordType: 'platform', recordId: platform.id, action: 'reviewed', notes })
    } catch (err) {
      console.error('Error reviewing platform:', err)
      throw err
    } finally {
      setSaving(false)
    }
  }

  // Review an access record (updates last_review date + creates audit entry)
  const reviewAccessRecord = async (record, notes) => {
    try {
      setSaving(true)
      const today = new Date().toISOString().slice(0, 10)
      const { data, error } = await supabase.from('access_records')
        .update({ last_review: today })
        .eq('id', record.id).select().single()
      if (error) throw error
      setAccessRecords(prev => prev.map(a => a.id === record.id ? toCamelCase(data) : a))
      await writeAuditLog({ recordType: 'access_record', recordId: record.id, action: 'reviewed', notes })
    } catch (err) {
      console.error('Error reviewing access record:', err)
      throw err
    } finally {
      setSaving(false)
    }
  }

  // Remove access with audit trail (deletes record + logs snapshot)
  const removeAccessRecord = async (record, reason) => {
    try {
      setSaving(true)
      const plat = platforms.find(p => p.id === record.platformId)
      const snapshot = {
        ...toSnakeCase(record),
        platform_name: plat?.name || 'Unknown',
      }
      const { error } = await supabase.from('access_records').delete().eq('id', record.id)
      if (error) throw error
      setAccessRecords(prev => prev.filter(a => a.id !== record.id))
      await writeAuditLog({ recordType: 'access_record', recordId: record.id, action: 'removed', notes: reason, snapshot })
    } catch (err) {
      console.error('Error removing access record:', err)
      throw err
    } finally {
      setSaving(false)
    }
  }

  // Remove platform with audit trail (deletes platform + associated access + logs snapshot)
  const removePlatform = async (platform, reason) => {
    try {
      setSaving(true)
      const snapshot = {
        ...toSnakeCase(platform),
        associated_access_count: accessRecords.filter(a => a.platformId === platform.id).length,
      }
      // Delete associated access records first
      const relatedAccess = accessRecords.filter(a => a.platformId === platform.id)
      if (relatedAccess.length > 0) {
        const { error: accErr } = await supabase.from('access_records').delete().eq('platform_id', platform.id)
        if (accErr) throw accErr
        setAccessRecords(prev => prev.filter(a => a.platformId !== platform.id))
      }
      // Delete the platform
      const { error } = await supabase.from('platforms').delete().eq('id', platform.id)
      if (error) throw error
      setPlatforms(prev => prev.filter(p => p.id !== platform.id))
      await writeAuditLog({ recordType: 'platform', recordId: platform.id, action: 'removed', notes: reason, snapshot })
    } catch (err) {
      console.error('Error removing platform:', err)
      throw err
    } finally {
      setSaving(false)
    }
  }

  const bulkImport = async (records, mode, table) => {
    const results = { inserted: 0, updated: 0, failed: 0, errors: [] }
    try {
      setSaving(true)

      if (mode === 'overwrite') {
        const { error: delErr } = await supabase.from(table).delete().eq('tenant_id', currentTenant.id)
        if (delErr) throw delErr

        // Bulk insert all
        for (let i = 0; i < records.length; i++) {
          try {
            const snakeData = cleanForDb(toSnakeCase(records[i]))
            const { id, created_at, updated_at, created_by, ...dataToSave } = snakeData
            dataToSave.tenant_id = currentTenant.id
            dataToSave.created_by = user.id
            const { error } = await supabase.from(table).insert(dataToSave)
            if (error) throw error
            results.inserted++
          } catch (err) {
            results.failed++
            results.errors.push({ row: i + 2, message: err.message })
          }
        }
      } else {
        // Merge mode
        for (let i = 0; i < records.length; i++) {
          try {
            const rec = records[i]
            const snakeData = cleanForDb(toSnakeCase(rec))
            const { id, created_at, updated_at, created_by, ...dataToSave } = snakeData
            dataToSave.tenant_id = currentTenant.id
            dataToSave.created_by = user.id

            let existing = null
            if (table === 'platforms') {
              const { data } = await supabase.from('platforms').select('id').eq('tenant_id', currentTenant.id).ilike('name', rec.name).maybeSingle()
              existing = data
            } else {
              const { data } = await supabase.from('access_records').select('id').eq('tenant_id', currentTenant.id).eq('platform_id', rec.platformId).ilike('role_name', rec.roleName).ilike('user_or_group', rec.userOrGroup).maybeSingle()
              existing = data
            }

            if (existing) {
              const { error } = await supabase.from(table).update(dataToSave).eq('id', existing.id)
              if (error) throw error
              results.updated++
            } else {
              const { error } = await supabase.from(table).insert(dataToSave)
              if (error) throw error
              results.inserted++
            }
          } catch (err) {
            results.failed++
            results.errors.push({ row: i + 2, message: err.message })
          }
        }
      }

      await fetchData()
      return results
    } catch (err) {
      console.error('Bulk import error:', err)
      throw err
    } finally {
      setSaving(false)
    }
  }

  const overdueAccessCount = accessRecords.filter(a => isOverdue(a.lastReview, a.reviewFrequency)).length
  const overduePlatformCount = platforms.filter(p => isOverdue(p.lastReview, p.reviewFrequency || 'Quarterly')).length
  const overdueCount = overdueAccessCount + overduePlatformCount

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    )
  }

  return (
    <div>
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-600 flex items-center justify-between">
          <span><strong>Error:</strong> {error}</span>
          <button onClick={fetchData} className="px-3 py-1.5 bg-red-600 text-white rounded-md text-sm hover:bg-red-700">Retry</button>
        </div>
      )}

      <SubViewTabs activeView={activeView} setActiveView={setActiveView} overdueCount={overdueCount} overduePlatformCount={overduePlatformCount} overdueAccessCount={overdueAccessCount} />

      {activeView === "dashboard" && <Dashboard platforms={platforms} accessRecords={accessRecords} setActiveView={setActiveView} />}
      {activeView === "platforms" && <PlatformRegistry platforms={platforms} onSave={savePlatform} onDelete={deletePlatform} onBulkImport={bulkImport} onReview={reviewPlatform} onRemove={removePlatform} accessRecords={accessRecords} auditLogs={auditLogs} saving={saving} />}
      {activeView === "access" && <AccessRegister accessRecords={accessRecords} onSave={saveAccessRecord} onDelete={deleteAccessRecord} onBulkImport={bulkImport} onReview={reviewAccessRecord} onRemove={removeAccessRecord} platforms={platforms} auditLogs={auditLogs} saving={saving} />}
    </div>
  )
}
